const AbstractPaymentMethodConfig = require('@/lib/PaymentMethods/AbstractPaymentMethodConfig');
const { splitCamelCase, toTitleCase } = require('@/functions/strings');
const { toNullableString, toString } = require('@/functions/transform');

class PayyoConfig extends AbstractPaymentMethodConfig {
    constructor() {
        super();

        /**
         * Currency string e.g. GBP.
         *
         * @type {string}
         */
        this.currency = 'GBP';

        /**
         * API key provided by Payyo.
         * This will be set at company level.
         *
         * @type {string}
         */
        this.apiKey = null;

        /**
         * Provided by Payyo. This identifies our integration.
         * This value will never change and is the same across all environments.
         *
         * @type {string}
         */
        this.platformId = null;

        /**
         * Provided by Payyo. This identifies our client.
         * This value will never change and is the same across all environments.
         *
         * @type {string}
         */
        this.merchantId = null;

        /**
         * The unique id of the terminal. These can be fetched from the /devices/:device_id endpoint.
         * Note Payyo support virtual / simulated devices.
         *
         * @type {string|null}
         */
        this.deviceId = null;

        /**
         * @type {string}
         */
        this.apiBaseUrl = 'https://api.pos.payyo.ch/';

        /**
         * @type {string}
         */
        this.apiSocketUrl = 'wss://ws.pos.payyo.ch/';

        /**
         * Development: test
         * Production: v1
         *
         * @type {string}
         */
        this.apiUrlPath = null;

        /**
         * Payyo / Stripe do not allow CNP payments in UK and Europe.
         *
         * @type {boolean}
         */
        this.allowCnp = false;

        /**
         * Polling period in milliseconds when a transaction is in progress.
         * Note: polling will be replaced by websockets.
         *
         * @type {number}
         */
        this.pollPeriod = 1000;
    }

    /**
     * Returns true if this is a valid configuration.
     * Otherwise, throws an error.
     *
     * @param {boolean} validateDeviceId
     *
     * @return {boolean}
     */
    validate(validateDeviceId = true) {
        let required = [
            // We need to be able to validate the config before the deviceId has been set so that we can make
            // a request to register or choose a terminal.
            validateDeviceId ? 'deviceId' : null,
            'apiKey',
            'apiUrlPath',
            'merchantId',
            'platformId',
        ].filter((v) => !!v);

        for (let i = 0; i < required.length; i++) {
            if (!this[required[i]]) {
                // Format the field nicely e.g. terminalId -> Terminal ID.
                const field = toTitleCase(splitCamelCase(required[i])).replace('Id', 'ID');
                throw new Error('The ' + field + ' value must be set.');
            }
        }

        return true;
    }

    getHydrationMap() {
        return {
            apiKey: toString,
            apiUrlPath: toString,
            platformId: toString,
            merchantId: toString,
            deviceId: toNullableString,
            currency: toString,
        };
    }
}

module.exports = PayyoConfig;
